import { QueryClient, QueryClientProvider, onlineManager } from "@tanstack/react-query";
import { RouterProvider } from "react-router";
import { HelmetProvider } from "react-helmet-async";
import { router } from "./routes";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useEffect, useState } from "react";
import { onAPIError, APIError, circuitBreaker } from "../services/api";
import { UpgradeModal } from "./components/UpgradeModal";
import { networkStatus } from "../services/networkStatus";

// ── React Query: smart retry ────────────────────────────────────────
// 4xx = user error → never retry. 5xx = already retried by apiClient interceptor.
// Only retry network errors (query fails without response).
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof APIError) {
    // User errors and rate limits: never retry at the query level
    if (error.category === 'user' || error.category === 'rate_limit' || error.category === 'auth') {
      return false;
    }
    // System errors (5xx): apiClient already retried 3 times — one more from React Query
    if (error.category === 'system') {
      return failureCount < 1;
    }
  }
  // Network errors: retry up to 2 times (React Query pauses when offline)
  return failureCount < 2;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always', // Auto-refetch stale queries on reconnect
      staleTime: 30000, // 30 seconds
    },
    mutations: {
      retry: false, // Mutations never auto-retry at query level (apiClient handles 5xx)
    },
  },
});

// ── Sync React Query's online manager with our network status ───────
onlineManager.setOnline(networkStatus.isOnline);
networkStatus.subscribe((isOnline) => {
  onlineManager.setOnline(isOnline);
  if (isOnline) {
    // Connection restored — reset circuit breaker and refetch everything stale
    circuitBreaker.reset();
    queryClient.invalidateQueries();
  }
});

export default function App() {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<"quota_exceeded" | "feature_locked" | "general">("general");

  // Listen for API errors globally
  useEffect(() => {
    const unsubscribe = onAPIError((error: APIError) => {
      if (error.status === 429 || error.code === 'USAGE_LIMIT_EXCEEDED') {
        setUpgradeReason("quota_exceeded");
        setShowUpgradeModal(true);
      } else if (error.status === 402 || error.code === 'UPGRADE_REQUIRED') {
        setUpgradeReason("feature_locked");
        setShowUpgradeModal(true);
      } else if (error.status === 401) {
        // Token expired or invalid — redirect to login
        if (!window.location.pathname.startsWith('/login') &&
            !window.location.pathname.startsWith('/auth')) {
          window.location.href = '/login';
        }
      }
    });

    return unsubscribe;
  }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />

          {/* Global Upgrade Modal for 429 / 402 Errors */}
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            reason={upgradeReason}
          />
        </QueryClientProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}